import { useState, useEffect } from 'react';
import * as ImagePicker from 'expo-image-picker';
import { Platform } from 'react-native';

export type UseMediaPermissionsReturnValue = {
    hasMediaPermissions: boolean;
    requestMediaPermissions: () => Promise<boolean>;
};

export default function useMediaPermissions(): UseMediaPermissionsReturnValue {
    const [hasMediaPermissions, setHasMediaPermissions] = useState<boolean>(false);

    const requestMediaPermissions = async (): Promise<boolean> => {
        if (Platform.OS === 'ios' || Platform.OS === 'android') {
            const cameraRollStatus = await ImagePicker.requestMediaLibraryPermissionsAsync();
            const cameraStatus = await ImagePicker.requestCameraPermissionsAsync();
            const permissionsGranted = cameraRollStatus.status === 'granted' && cameraStatus.status === 'granted';
            setHasMediaPermissions(permissionsGranted);
            return permissionsGranted;
        } else {
            setHasMediaPermissions(true);
            return true;
        }
    };

    useEffect(() => {
        (async () => {
            if (Platform.OS === 'ios' || Platform.OS === 'android') {
                const cameraRollStatus = await ImagePicker.getMediaLibraryPermissionsAsync();
                const cameraStatus = await ImagePicker.getCameraPermissionsAsync();
                const permissionsGranted = cameraRollStatus.status === 'granted' && cameraStatus.status === 'granted';
                setHasMediaPermissions(permissionsGranted);
            } else {
                setHasMediaPermissions(true);
            }
        })();
    }, []);

    return {
        hasMediaPermissions,
        requestMediaPermissions,
    };
}

export function usePermissionGuardedAction<T extends (...args: any[]) => any>(
    action: T,
): (...args: Parameters<T>) => Promise<void> {
    const { hasMediaPermissions, requestMediaPermissions } = useMediaPermissions();

    return async (...args: Parameters<T>) => {
        if (!hasMediaPermissions) {
            const permissionsGranted = await requestMediaPermissions();
            if (!permissionsGranted) {
                return;
            }
        }

        action(...args);
    };
}
